import { type RefObject, useCallback, useState } from 'react';
import { useToast } from '@adc/parallax-component-library';
import { IconStatusIncorrect } from '@adc/parallax-icons';

import { useAdminUserCreateMutation } from '~/api/query/adminUserCreate';
import { QUERY_KEY } from '~/api/query/keys';
import { queryClient } from '~/api/query/queryClient';
import type {
  AdminUserFormRef,
  UserSubmitValues,
} from '~/components/forms/adminUser/AdminUserForm';
import { useModal } from '~/features/modals/useModal';

type UseAdminUserCreateFormOptions = {
  formRef: RefObject<AdminUserFormRef | null>;
  onViewUserProfile: (userId: string) => Promise<void>;
};

export const useAdminUserCreateForm = ({
  formRef,
  onViewUserProfile,
}: UseAdminUserCreateFormOptions) => {
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const createUserMutation = useAdminUserCreateMutation();
  const [isNextStepDisabled, setIsNextStepDisabled] = useState(false);

  const handleSubmitClick = useCallback(async () => {
    await formRef.current?.submitForm();
  }, [formRef]);

  const handleCreateUser = useCallback(
    async (values: UserSubmitValues) => {
      setIsNextStepDisabled(true);
      try {
        const newAdminUser = await createUserMutation.mutateAsync(values);

        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.USER_SEARCH],
        });

        closeModal('AdminUserForm');

        openModal('AdminUserAdded', {
          userId: newAdminUser.id,
          userFullName: `${newAdminUser.firstName} ${newAdminUser.lastName}`,
          onViewUserProfile,
          testID: 'admin-user-added-modal',
        });
      } catch (error) {
        console.error('Error creating admin user:', error);

        showToast({
          message: `There was an error creating the user: ${error instanceof Error ? error.message : 'Unknown error'}`,
          icon: <IconStatusIncorrect accentColor="$support.danger.default" />,
        });
      } finally {
        setIsNextStepDisabled(false);
      }
    },
    [closeModal, openModal, createUserMutation, showToast, onViewUserProfile],
  );

  return {
    handleSubmitClick,
    handleCreateUser,
    isFormLoading: createUserMutation.isPending,
    isNextStepDisabled,
  };
};
